import { queryProducts, queryProduct, queryCollection, queryCheckout, mutateCreateCheckout, mutateCheckoutItems, mutateCartAdd } from './shopify-queries'
import supportsWebP from '@/plugins/supportsWebP'
import get from 'lodash/get'
import commaNum from '@/plugins/commaSeparatedNumber'
import euCountries from './eu-countries'

// tokens
const shopifyPublicToken = process.env.VUE_APP_SHOPIFY_TOKEN_PUBLIC // for "public" products, used for product results / collection pages
const shopifyMasterToken = process.env.VUE_APP_SHOPIFY_TOKEN // checkouts, getProduct, ...

const shopifyBaseUrl = 'https://' + process.env.VUE_APP_SHOPIFY_DOMAIN
const sortDefault = 'recommended'
const imgFrmt = supportsWebP() ? 'WEBP' : 'JPG'

export default {
  namespaced: true,
  state: () => ({
    checkout: null,
    checkoutUpdating: false,
    collections: [],
    querying: false,
    mblFilters: false,
    sort: sortDefault,
    sortOptions: [
      { label: 'Recommended', handle: 'recommended', key: 'UPDATED_AT', keyColl: 'COLLECTION_DEFAULT', reverse: false },
      { label: 'Newest', handle: 'newest', key: 'CREATED_AT', keyColl: 'CREATED', reverse: true },
      // {label: 'Oldest', handle: 'oldest', key: 'CREATED_AT', reverse: true},
      { label: 'Price - Low to High', handle: 'price', key: 'PRICE', keyColl: 'PRICE', reverse: false },
      { label: 'Price - High to Low', handle: 'price-desc', key: 'PRICE', keyColl: 'PRICE', reverse: true }
    ],
    countries: [],
    country: null,
    euVAT: process.env.VUE_APP_SHOPIFY_VAT_EU // set manually because Shopify API doesn't add to EU countries properly.
  }),
  getters: {
    cartTotal (state) {
      return !state.checkout ? 0 : state.checkout.lines.reduce((acc, cur) => acc + cur.quantity, 0)
    },
    country (state) {
      const shippingCountry = get(state, 'checkout.shippingAddress.countryCodeV2')
      return shippingCountry || state.country || 'GB'
    },
    countryTax (state, getters) {
      if (!state.countries.length) return 0
      let region = state.countries.find(region => region.code === getters.country)
      // manually set EU
      region = euCountries.includes(getters.country) ? { tax: state.euVAT } : region
      // rest of world
      region = region || state.countries.find(region => region.code === '*')
      return region && region.tax ? region.tax : 0
    },
    price: (state, getters) => (amnt) => {
      if (isNaN(amnt)) return 'N/A'
      amnt = Number(amnt) + Number(amnt) * getters.countryTax // add tax
      return '£' + commaNum(Number(amnt).toFixed(2).toString())
    },
    nav: (state) => (handle) => {
      const itms = [
        { txt: 'Hostem', rt: '/hostem' },
        { txt: 'All Products', rt: { name: 'products' } }
      ]
      if (handle) {
        const col = state.collections.find(c => c.handle === handle)
        itms.push({
          txt: col ? col.title : `<span class="capitalize">${handle.replaceAll('-', ' ')}</span>`,
          rt: { name: 'products_collection', params: { collection: handle } }
        })
      }
      return itms
    },
    activeSort (state) {
      return state.sortOptions.find(opt => opt.handle === state.sort) || state.sortOptions[0]
    }
  },
  mutations: {
    setQuerying (state, bool = false) {
      state.querying = bool
    },
    setCheckout (state, chk) {
      // format
      chk.lines = chk.lines.edges.map(edge => edge.node)
      // save
      state.checkout = chk
      localStorage.setItem('checkoutId', chk.id)
    },
    clearCheckout (state) {
      state.checkout = null
    },
    setCheckoutUpdating (state, bool = true) {
      state.checkoutUpdating = bool
    },
    saveCollection (state, doc) {
      // filter out "sold-out" products (can't do by collections query...)
      doc.products = doc.products.filter(prd => prd.availableForSale !== false)

      // if saved... append products to existing
      const ii = state.collections.findIndex(c => c.handle === doc.handle)
      if (ii > -1) {
        // clone saved collection and re-push at end so data reacts...
        const saved = { ...state.collections[ii] }
        // append new products
        saved.products = saved.products.concat(doc.products)
        // remove old
        state.collections.splice(ii, 1)
        // save
        return state.collections.push(saved)
      }
      // ...else push new
      state.collections.push(doc)
    },
    setMblFilters (state, bool = false) {
      state.mblFilters = bool
    },
    setSort (state, handle) {
      state.sort = handle || sortDefault
      state.collections = [] // flush saved collections, to requery based on new sort...
    },
    setCountries (state, arr) {
      state.countries = arr
    },
    setCountry (state, val) {
      console.log('country', val)
      state.country = val
    }
  },
  actions: {
    async getProducts ({ getters }, { first = 10, query = '', after, fullBody = false }) {
      // only query for-sale items
      query = query && query.length ? 'available_for_sale:true AND ' + query : 'available_for_sale:true'

      // always reverse if sorting by updated to get latest
      const reverse = getters.activeSort.key === 'UPDATED_AT' ? true : getters.activeSort.reverse

      const qry = {
        query: queryProducts(fullBody),
        variables: { first, query, after, imgMaxW: 1280, sortKey: getters.activeSort.key, reverse: reverse }
      }
      const resp = await shopify(qry).catch(onErr)
      return resp && resp.data.products
    },

    async getProductsFromCollection ({ getters, commit, dispatch }, { handle, first = 40, after }) {
      const qry = {
        query: queryCollection,
        variables: { handle, first, after, imgMaxW: 1280, sortKey: getters.activeSort.keyColl, reverse: getters.activeSort.reverse }
      }
      const resp = await shopify(qry).catch(onErr)
      const doc = get(resp, 'data.collectionByHandle')
      if (doc) {
        // if more products, fetch next page to add to the end
        if (doc.products.pageInfo.hasNextPage) {
          const cursor = doc.products.edges[doc.products.edges.length - 1].cursor
          dispatch('getProductsFromCollection', { handle, after: cursor, first: 250 })
        }
        // save
        doc.products = doc.products.edges.map(edge => edge.node)
        commit('saveCollection', doc)
        return doc.products
      }
      return []
    },

    async getProduct ({ commit }, { handle, imgMaxW = 1280, showroom = false }) {
      const qry = {
        query: queryProduct,
        variables: { handle, imgMaxW, imgFrmt: imgFrmt },
        token: showroom ? shopifyMasterToken : shopifyPublicToken
      }
      const resp = await shopify(qry).catch(onErr)
      return resp && resp.data.productByHandle
    },

    async fetchCheckout ({ commit }) {
      const id = localStorage.getItem('checkoutId')
      if (!id) return null
      const qry = {
        query: queryCheckout,
        variables: { id },
        token: shopifyMasterToken
      }
      const resp = await shopify(qry).catch(onErr)
      if (resp.errors) {
        console.error(resp.errors)
        return Promise.reject(new Error('fetchCheckout query had an error'))
      }
      const chk = get(resp, 'data.node')
      // missing or completed
      if (!chk || chk.completedAt) {
        console.log('Missing cart or completed:', (chk && chk.completedAt))
        localStorage.removeItem('checkoutId')
        commit('clearCheckout') // reset
        return null
      }
      // save
      commit('setCheckout', chk)
      return chk
    },

    async newCheckout ({ commit }, lines) {
      const qry = {
        query: mutateCreateCheckout,
        variables: { input: { lines } },
        token: shopifyMasterToken
      }
      commit('setCheckoutUpdating')
      let resp = await shopify(qry).catch(onErr)

      const chk = get(resp, 'data.cartCreate.cart')

      if (chk) {
        commit('setCheckout', chk)
        return chk
      }
      commit('setCheckoutUpdating', false)
      return false
    },

    async updateCheckoutItems ({ state, commit, dispatch }, lines) {
      if (!state.checkout) return dispatch('newCheckout', lines)

      const qry = {
        query: mutateCheckoutItems,
        variables: { id: state.checkout.id, lines },
        token: shopifyMasterToken
      }

      commit('setCheckoutUpdating')
      let resp = await shopify(qry).catch(onErr)

      if (resp.errors) onErr(resp.errors)
      const chk = get(resp, 'data.cartLinesUpdate.cart')

      if (chk) {
        commit('setCheckout', chk)
      }
      commit('setCheckoutUpdating', false)
      return !!chk
    },

    async addItemToCheckout ({ state, commit, dispatch }, { merchandiseId, quantity }) {
      // no checkout? create new with items...
      const lines = [{ merchandiseId, quantity }]
      if (!state.checkout) return dispatch('newCheckout', lines)
      // check if variant exists in checkout...

      const qry = {
        query: mutateCartAdd,
        variables: { id: state.checkout.id, lines },
        token: shopifyMasterToken
      }

      commit('setCheckoutUpdating')
      let resp = await shopify(qry).catch(onErr)

      if (resp.errors) onErr(resp.errors)
      const chk = get(resp, 'data.cartLinesAdd.cart')
      if (chk) {
        commit('setCheckout', chk)
      }
      commit('setCheckoutUpdating', false)
      return !!chk
    },

    // countries for tax calc
    async getCountries ({ commit }) {
      fetch('/api/countries')
        .then(resp => resp.json())
        .then(json => commit('setCountries', json))
        .catch(err => onErr(err))
    },

    async getUserCountry ({ commit }) {
      return fetch('/api/country').then(resp => resp.json()).then(country => commit('setCountry', country))
    }
  }
}

// helpers
const shopify = ({ query, variables, token }) => {
  // default to public token
  token = token || shopifyPublicToken
  // go
  return fetch(shopifyBaseUrl + '/api/2024-07/graphql', { // 2019-07
    method: 'POST',
    headers: {
      'X-Shopify-Storefront-Access-Token': token,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ query, variables })
  })
    .then(async resp => {
      const json = await resp.json()
      // console.log({ query, variables, json })
      return json
    })
    .then(data => {
      if (data.errors) {
        console.error(data.errors)
        return Promise.reject(new Error('Shopify API Response Errors'))
      }
      return data
    })
    .catch(e => console.error('Shopify API Error:', { error: e, query, variables }))
}
// error handler
const onErr = e => console.error('Shopify API Error:', e)
